// /admin/mi-cuenta — pantalla de perfil del usuario logado.
// Dos secciones:
//  1) Datos del perfil — nombre/apellidos/idioma editables, email/rol read-only
//  2) Cambiar contraseña — actual + nueva + confirmar, policy live (12+ + mayús
//     + minús + dígito + símbolo), valida actual server-side antes de aplicar.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../lib/api';

const IDIOMAS = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
  { value: 'ru', label: 'Русский' },
  { value: 'fr', label: 'Français' },
];

function checkPasswordPolicy(p) {
  return {
    length: p.length >= 12,
    upper: /[A-Z]/.test(p),
    lower: /[a-z]/.test(p),
    digit: /[0-9]/.test(p),
    symbol: /[^A-Za-z0-9]/.test(p),
  };
}

export default function AdminMiCuenta() {
  const { t, i18n } = useTranslation();
  const { user, updateMyProfile, changeMyPassword } = useAuth();

  // ---- Sección 1: perfil ----
  const [profile, setProfile] = useState({
    nombre: '',
    apellidos: '',
    telefono: '',
    idioma_preferido: 'es',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileOk, setProfileOk] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        nombre: user.nombre || '',
        apellidos: user.apellidos || '',
        telefono: user.telefono || '',
        idioma_preferido: user.idioma_preferido || 'es',
      });
    }
  }, [user]);

  const submitProfile = async (e) => {
    e.preventDefault();
    setProfileError(null);
    setProfileOk(false);
    if (!profile.nombre.trim()) {
      setProfileError(t('admin.mi_cuenta.errores.nombre_requerido'));
      return;
    }
    setProfileSaving(true);
    try {
      const updated = await updateMyProfile({
        nombre: profile.nombre.trim(),
        apellidos: profile.apellidos.trim() || null,
        telefono: profile.telefono.trim() || null,
        idioma_preferido: profile.idioma_preferido,
      });
      setProfileOk(true);
      // Si el idioma cambió y es uno soportado, sincroniza la UI con el nuevo
      if (updated?.idioma_preferido && updated.idioma_preferido !== i18n.resolvedLanguage) {
        i18n.changeLanguage(updated.idioma_preferido);
      }
      // Auto-clear success message
      setTimeout(() => setProfileOk(false), 3500);
    } catch (err) {
      if (err instanceof ApiError) {
        setProfileError(err.message || t('admin.mi_cuenta.errores.generico'));
      } else {
        setProfileError(t('admin.mi_cuenta.errores.generico'));
      }
    } finally {
      setProfileSaving(false);
    }
  };

  // ---- Sección 2: cambiar password ----
  const [passActual, setPassActual] = useState('');
  const [passNueva, setPassNueva] = useState('');
  const [passConfirma, setPassConfirma] = useState('');
  const [passSaving, setPassSaving] = useState(false);
  const [passError, setPassError] = useState(null);
  const [passOk, setPassOk] = useState(false);

  const policy = checkPasswordPolicy(passNueva);
  const policyOk = policy.length && policy.upper && policy.lower && policy.digit && policy.symbol;
  const matches = passNueva.length > 0 && passNueva === passConfirma;
  const distinct = passNueva.length > 0 && passNueva !== passActual;

  const submitPassword = async (e) => {
    e.preventDefault();
    setPassError(null);
    setPassOk(false);
    if (!passActual) {
      setPassError(t('admin.mi_cuenta.errores.password_actual_requerida'));
      return;
    }
    if (!policyOk) {
      setPassError(t('admin.mi_cuenta.errores.password_debil'));
      return;
    }
    if (!matches) {
      setPassError(t('admin.mi_cuenta.errores.passwords_no_coinciden'));
      return;
    }
    if (!distinct) {
      setPassError(t('admin.mi_cuenta.errores.password_repetida'));
      return;
    }
    setPassSaving(true);
    try {
      await changeMyPassword({ password_actual: passActual, password_nueva: passNueva });
      setPassOk(true);
      setPassActual('');
      setPassNueva('');
      setPassConfirma('');
      setTimeout(() => setPassOk(false), 4500);
    } catch (err) {
      if (err instanceof ApiError) {
        const traduccion = t('admin.mi_cuenta.errores.' + err.code, { defaultValue: '' });
        setPassError(traduccion || err.message || t('admin.mi_cuenta.errores.generico'));
      } else {
        setPassError(t('admin.mi_cuenta.errores.generico'));
      }
    } finally {
      setPassSaving(false);
    }
  };

  return (
    <div className="admin-mi-cuenta">
      <header className="admin-page-header">
        <h1>{t('admin.mi_cuenta.titulo')}</h1>
        <p className="admin-page-sub">{t('admin.mi_cuenta.sub')}</p>
      </header>

      {/* Sección 1: perfil */}
      <section className="admin-card">
        <h2 className="admin-card-titulo">{t('admin.mi_cuenta.perfil_titulo')}</h2>
        <p className="admin-card-sub">{t('admin.mi_cuenta.perfil_sub')}</p>

        <form className="admin-form" onSubmit={submitProfile} noValidate>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.mi_cuenta.email')}</label>
              <input type="email" value={user?.email || ''} disabled readOnly />
            </div>
            <div className="modal-campo">
              <label>{t('admin.mi_cuenta.rol')}</label>
              <input type="text" value={user?.rol || ''} disabled readOnly />
            </div>
            <div className="modal-campo">
              <label htmlFor="mc-nombre">{t('admin.mi_cuenta.nombre')}</label>
              <input
                id="mc-nombre"
                type="text"
                value={profile.nombre}
                onChange={(e) => setProfile((p) => ({ ...p, nombre: e.target.value }))}
                disabled={profileSaving}
                required
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="mc-apellidos">{t('admin.mi_cuenta.apellidos')}</label>
              <input
                id="mc-apellidos"
                type="text"
                value={profile.apellidos}
                onChange={(e) => setProfile((p) => ({ ...p, apellidos: e.target.value }))}
                disabled={profileSaving}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="mc-telefono">{t('admin.mi_cuenta.telefono')}</label>
              <input
                id="mc-telefono"
                type="tel"
                value={profile.telefono}
                onChange={(e) => setProfile((p) => ({ ...p, telefono: e.target.value }))}
                disabled={profileSaving}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="mc-idioma">{t('admin.mi_cuenta.idioma')}</label>
              <select
                id="mc-idioma"
                value={profile.idioma_preferido}
                onChange={(e) => setProfile((p) => ({ ...p, idioma_preferido: e.target.value }))}
                disabled={profileSaving}
              >
                {IDIOMAS.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>

          {profileError && (
            <div className="modal-error-general">
              <AlertCircle size={16} strokeWidth={1.75} />
              <span>{profileError}</span>
            </div>
          )}
          {profileOk && (
            <div className="admin-msg-ok">
              <CheckCircle2 size={16} strokeWidth={1.75} />
              <span>{t('admin.mi_cuenta.perfil_guardado')}</span>
            </div>
          )}

          <div className="admin-form-actions">
            <button type="submit" className="btn-primary" disabled={profileSaving}>
              {profileSaving ? (
                <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('admin.mi_cuenta.guardando')}</>
              ) : (
                t('admin.mi_cuenta.guardar_perfil')
              )}
            </button>
          </div>
        </form>
      </section>

      {/* Sección 2: password */}
      <section className="admin-card">
        <h2 className="admin-card-titulo">{t('admin.mi_cuenta.password_titulo')}</h2>
        <p className="admin-card-sub">{t('admin.mi_cuenta.password_sub')}</p>

        <form className="admin-form" onSubmit={submitPassword} noValidate>
          <div className="admin-form-grid one-col">
            <div className="modal-campo">
              <label htmlFor="mc-pass-actual">{t('admin.mi_cuenta.password_actual')}</label>
              <input
                id="mc-pass-actual"
                type="password"
                autoComplete="current-password"
                value={passActual}
                onChange={(e) => setPassActual(e.target.value)}
                disabled={passSaving}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="mc-pass-nueva">{t('admin.mi_cuenta.password_nueva')}</label>
              <input
                id="mc-pass-nueva"
                type="password"
                autoComplete="new-password"
                value={passNueva}
                onChange={(e) => setPassNueva(e.target.value)}
                disabled={passSaving}
              />
            </div>
            <div className="modal-campo">
              <label htmlFor="mc-pass-confirma">{t('admin.mi_cuenta.password_confirma')}</label>
              <input
                id="mc-pass-confirma"
                type="password"
                autoComplete="new-password"
                value={passConfirma}
                onChange={(e) => setPassConfirma(e.target.value)}
                disabled={passSaving}
              />
            </div>
          </div>

          <ul className="login-policy">
            <li className={policy.length ? 'ok' : ''}>{t('admin.reset.req_length')}</li>
            <li className={policy.upper ? 'ok' : ''}>{t('admin.reset.req_upper')}</li>
            <li className={policy.lower ? 'ok' : ''}>{t('admin.reset.req_lower')}</li>
            <li className={policy.digit ? 'ok' : ''}>{t('admin.reset.req_digit')}</li>
            <li className={policy.symbol ? 'ok' : ''}>{t('admin.reset.req_symbol')}</li>
          </ul>

          {passError && (
            <div className="modal-error-general">
              <AlertCircle size={16} strokeWidth={1.75} />
              <span>{passError}</span>
            </div>
          )}
          {passOk && (
            <div className="admin-msg-ok">
              <CheckCircle2 size={16} strokeWidth={1.75} />
              <span>{t('admin.mi_cuenta.password_actualizada')}</span>
            </div>
          )}

          <div className="admin-form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={passSaving || !passActual || !policyOk || !matches}
            >
              {passSaving ? (
                <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('admin.mi_cuenta.guardando')}</>
              ) : (
                t('admin.mi_cuenta.cambiar_password')
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
